import { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import { useGoogleAuth } from '../hooks/useGoogleAuth';
import AuthButton from './AuthButton';
import AuthPaper from './AuthPaper';

/**
 * GoogleAuth component handles OAuth callback processing
 * Detects code and state parameters from URL and processes authentication
 */
const GoogleAuth = () => {
  const [searchParams] = useSearchParams();
  const [callbackFailed, setCallbackFailed] = useState(false);
  const hasAttemptedAuth = useRef(false);

  // Hook for Google authentication with code and state
  const googleAuthMutation = useGoogleAuth();

  // Get OAuth parameters
  const code = searchParams.get('code');
  const state = searchParams.get('state');

  // Return null if no OAuth callback parameters are present
  if (!code || !state) {
    return null;
  }

  // Check if this is an OAuth callback (has code and state parameters)
  useEffect(() => {
    if (code && state && !hasAttemptedAuth.current && !googleAuthMutation.isPending) {
      hasAttemptedAuth.current = true;
      // Call Google authentication API with code and state
      googleAuthMutation.mutate({ code, state });
    }
  }, [code, state, googleAuthMutation.mutate, googleAuthMutation.isPending]);

  // Handle authentication success/error
  useEffect(() => {
    if (googleAuthMutation.isSuccess) {
      // onAuthSuccess
    }

    if (googleAuthMutation.isError) {
      setCallbackFailed(true);
      // onAuthError?.(googleAuthMutation.error as Error);
    }
  }, [googleAuthMutation.isSuccess, googleAuthMutation.isError, googleAuthMutation.error]);

  // Processing OAuth callback - show loading state
  if (googleAuthMutation.isPending && !callbackFailed) {
    return (
      <Stack
        justifyContent={'center'}
        alignItems={'center'}
        sx={{
          color: 'white',
          minHeight: '100vh',
        }}
        data-testid="google-auth-loading"
      >
        <CircularProgress size={60} sx={{ mt: 4 }} />
        <Typography
          mt={2}
          sx={{
            fontSize: '18px',
          }}
        >
          {googleAuthMutation.isPending ? 'Authenticating...' : 'Please wait...'}
        </Typography>
      </Stack>
    );
  }

  // OAuth callback failed - show error state
  if (callbackFailed) {
    return (
      <Stack
        justifyContent={'center'}
        alignItems={'center'}
        sx={{
          color: 'white',
          minHeight: '100vh',
        }}
        data-testid="google-auth-error"
      >
        <AuthPaper>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h5"
              sx={{
                mb: 2,
                color: 'primary.main',
                fontWeight: '400',
                textAlign: 'center',
              }}
            >
              Authentication failed. Please try again.
            </Typography>

            <AuthButton
              width="60%"
              onClick={() => {
                setCallbackFailed(false);
                hasAttemptedAuth.current = false;
                // Clear URL parameters
                window.history.replaceState({}, document.title, '/login');
              }}
            >
              Try Again
            </AuthButton>
          </Box>
        </AuthPaper>
      </Stack>
    );
  }

  return null;
};

export default GoogleAuth;
